import { useEffect, useEffectEvent } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';

import type { LeaseDetailsFormValues } from './lease-details.defaults';

export type FormLifecycleCallbacks = {
  /** Fires whenever the form's dirty state flips, and with `false` when the form unmounts. */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Fires on unmount with the current values, only if the user changed something. */
  onUnmountDirty?: (values: LeaseDetailsFormValues) => void;
};

/**
 * Reports form-level lifecycle to the consumer. Its own leaf with a scoped `useFormState`, so a
 * dirty flip re-renders only this component — never the form root or its fields.
 */
export function FormLifecycleReporter({ onDirtyChange, onUnmountDirty }: FormLifecycleCallbacks) {
  const { control, getValues } = useFormContext<LeaseDetailsFormValues>();
  const { isDirty } = useFormState({ control });

  const reportDirty = useEffectEvent((dirty: boolean) => onDirtyChange?.(dirty));
  // Effect events see the latest render, and this component re-renders on every dirty flip.
  const reportUnmount = useEffectEvent(() => {
    onDirtyChange?.(false);
    if (isDirty) {
      onUnmountDirty?.(getValues());
    }
  });

  useEffect(() => {
    reportDirty(isDirty);
  }, [isDirty]);

  useEffect(() => () => reportUnmount(), []);

  return null;
}
